import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, Box, Button, Typography, IconButton, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckIcon from '@mui/icons-material/Check';

const ACCENT = '#0e7c86';
const ACCENT_DARK = '#0a5a62';

// Convert a data URL (from a canvas capture) into a File for multipart upload.
export function dataUrlToFile(dataUrl, filename) {
  const [head, b64] = dataUrl.split(',');
  const mime = (head.match(/:(.*?);/) || [null, 'image/jpeg'])[1];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], filename, { type: mime });
}

// Live camera capture in a dialog. onCapture(dataUrl) fires on "Use photo".
// Works on desktop (webcam) and mobile (rear camera via facingMode). Requires a
// secure context (HTTPS or localhost) — getUserMedia is blocked otherwise.
export default function CameraCapture({ open, onClose, onCapture, title = 'Take a photo' }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shot, setShot] = useState(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setShot(null); setError(null); setReady(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera is not available in this browser. Please use Upload instead.');
      return undefined;
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((stream) => {
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; setReady(true); }
      })
      .catch(() => setError('Could not access the camera. Please allow camera permission, or use Upload instead.'));
    return () => { active = false; stop(); };
  }, [open, stop]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    setShot(c.toDataURL('image/jpeg', 0.92));
  };
  const close = () => { stop(); setShot(null); setError(null); onClose(); };
  const use = () => { const s = shot; stop(); setShot(null); onCapture(s); onClose(); };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{title}</Typography>
        <IconButton size="small" onClick={close}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        {error ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography sx={{ color: '#ce4b4b', fontSize: 14 }}>{error}</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ position: 'relative', bgcolor: '#000', borderRadius: 2, overflow: 'hidden', minHeight: 240 }}>
              {shot ? (
                <img src={shot} alt="captured" style={{ width: '100%', display: 'block' }} />
              ) : (
                <video ref={videoRef} autoPlay playsInline muted
                  style={{ width: '100%', display: 'block' }} />
              )}
            </Box>
            <Stack direction="row" spacing={1.2} justifyContent="center" sx={{ mt: 2 }}>
              {shot ? (
                <>
                  <Button onClick={() => setShot(null)} startIcon={<ReplayIcon />}
                    sx={{ textTransform: 'none', color: '#64757d' }}>Retake</Button>
                  <Button onClick={use} variant="contained" startIcon={<CheckIcon />}
                    sx={{ textTransform: 'none', fontWeight: 700, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK } }}>
                    Use photo
                  </Button>
                </>
              ) : (
                <Button onClick={capture} variant="contained" disabled={!ready} startIcon={<CameraAltIcon />}
                  sx={{ textTransform: 'none', fontWeight: 700, px: 3, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK } }}>
                  Capture
                </Button>
              )}
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
